import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Flame, Sparkles, LogOut, User, Trophy, LibraryBig, Home, Plus, Award, ShieldCheck } from "lucide-react";
import { useAuth } from "../lib/auth";

const NavItem = ({ to, icon: Icon, label, active, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 border-brand-ink font-bold text-sm transition-transform hover:-translate-y-0.5 ${
      active ? "bg-brand-yellow shadow-brutal" : "bg-white hover:bg-brand-peach"
    }`}
  >
    <Icon className="w-4 h-4" strokeWidth={2.5} />
    <span className="hidden md:inline">{label}</span>
  </Link>
);

export default function Nav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();

  return (
    <nav className="sticky top-0 z-40 bg-brand-cream/95 backdrop-blur-sm border-b-2 border-brand-ink">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-4">
        <Link to={user ? "/dashboard" : "/"} data-testid="nav-logo" className="flex items-center gap-2 group">
          <div className="w-10 h-10 rounded-2xl bg-brand-yellow border-2 border-brand-ink shadow-brutal flex items-center justify-center group-hover:animate-pop">
            <span className="font-display font-bold text-xl text-brand-navy">L</span>
          </div>
          <span className="font-display font-bold text-2xl text-brand-navy">LingoFacil</span>
        </Link>

        {user ? (
          <div className="flex items-center gap-2 md:gap-3">
            <NavItem to="/dashboard" icon={Home} label="Home" active={pathname === "/dashboard"} testId="nav-home" />
            <NavItem to="/courses" icon={LibraryBig} label="Courses" active={pathname.startsWith("/courses")} testId="nav-courses" />
            <NavItem to="/leaderboard" icon={Trophy} label="Ranks" active={pathname === "/leaderboard"} testId="nav-leaderboard" />
            <NavItem to="/achievements" icon={Award} label="Badges" active={pathname === "/achievements"} testId="nav-achievements" />
            {user.is_admin && (
              <NavItem to="/create" icon={Plus} label="Create" active={pathname === "/create"} testId="nav-create" />
            )}
            {user.is_admin && (
              <NavItem to="/admin" icon={ShieldCheck} label="Admin" active={pathname === "/admin"} testId="nav-admin" />
            )}

            <div className="hidden lg:flex items-center gap-2 ml-2 pl-3 border-l-2 border-brand-ink">
              <div data-testid="nav-streak" className="flex items-center gap-1 bg-brand-pink text-white brutal-badge">
                <Flame className="w-4 h-4" strokeWidth={2.5} />
                {user.streak || 0}
              </div>
              <div data-testid="nav-xp" className="flex items-center gap-1 bg-brand-mint text-brand-navy brutal-badge">
                <Sparkles className="w-4 h-4" strokeWidth={2.5} />
                {user.xp || 0} XP
              </div>
            </div>

            <Link to="/profile" data-testid="nav-profile" className="w-10 h-10 rounded-full bg-brand-blue text-white border-2 border-brand-ink shadow-brutal flex items-center justify-center font-bold hover:-translate-y-0.5">
              <User className="w-5 h-5" strokeWidth={2.5} />
            </Link>
            <button
              onClick={() => { logout(); nav("/"); }}
              data-testid="nav-logout"
              className="hidden md:inline-flex w-10 h-10 rounded-full bg-white text-brand-navy border-2 border-brand-ink shadow-brutal items-center justify-center hover:-translate-y-0.5"
              title="Logout"
            >
              <LogOut className="w-5 h-5" strokeWidth={2.5} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login" className="brutal-btn-pink text-sm px-5 py-2">
              Log in
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
}
